import { For, Show, createResource, createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useApi, isOk } from '../api';
import type { SchematicLicenseDto } from '../api';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { formatDate, formatDateTime } from '../services/dateFormat';
import styles from './SchematicLicenses.module.css';
import { firstError } from '../api/envelope';

type LicenseStatus = 'active' | 'expired' | 'disabled';

function statusOf(entry: SchematicLicenseDto): LicenseStatus {
    if (!entry.isEnabled) return 'disabled';
    if (entry.expiresAt && new Date(entry.expiresAt).getTime() < Date.now()) return 'expired';
    return 'active';
}

const STATUS_KEYS: Record<LicenseStatus, string> = {
    active: 'SCHEMATIC_LICENSES_STATUS_ACTIVE',
    expired: 'SCHEMATIC_LICENSES_STATUS_EXPIRED',
    disabled: 'SCHEMATIC_LICENSES_STATUS_DISABLED',
};

interface LicenseForm {
    playerName: string;
    expiresAt: string;
    isEnabled: boolean;
    note: string;
}

const emptyForm = (): LicenseForm => ({
    playerName: '',
    expiresAt: '',
    isEnabled: true,
    note: '',
});

export default function SchematicLicenses() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [reloadToken, setReloadToken] = createSignal(0);
    const [form, setForm] = createStore<LicenseForm>(emptyForm());
    const [editingId, setEditingId] = createSignal<string | null>(null);
    const [submitting, setSubmitting] = createSignal(false);

    const [licenses] = createResource(
        reloadToken,
        async (): Promise<SchematicLicenseDto[]> => {
            try {
                const res = await api.schematicLicenseAdmin.listEntries();
                if (isOk(res.data)) return res.data.value.licenses ?? [];
            } catch { /* fall through to the toast */ }
            toast?.error(t('SCHEMATIC_LICENSES_FETCH_FAILED'));
            return [];
        }
    );

    const loading = () => licenses.state === 'pending';
    const reload = () => setReloadToken((n) => n + 1);

    const resetForm = () => {
        setForm(emptyForm());
        setEditingId(null);
    };

    const startEdit = (entry: SchematicLicenseDto) => {
        setForm({
            playerName: entry.currentUsername,
            expiresAt: entry.expiresAt ? entry.expiresAt.slice(0, 10) : '',
            isEnabled: entry.isEnabled,
            note: entry.note ?? '',
        });
        setEditingId(entry.id);
    };

    const submit = async () => {
        const id = editingId();
        const expiresAt = form.expiresAt ? new Date(form.expiresAt).toISOString() : null;
        const note = form.note.trim() ? form.note.trim() : null;

        setSubmitting(true);
        try {
            const res = id
                ? await api.schematicLicenseAdmin.updateEntry(id, { isEnabled: form.isEnabled, expiresAt, note })
                : await api.schematicLicenseAdmin.createEntry({ playerName: form.playerName.trim(), expiresAt, note });

            if (isOk(res.data)) {
                toast?.info(id ? t('SCHEMATIC_LICENSES_UPDATED') : t('SCHEMATIC_LICENSES_ADDED'));
                resetForm();
                reload();
            } else {
                // Keeps what was typed: the usual failure is an unresolvable name worth correcting.
                toast?.error(firstError(res.data) ?? t('SCHEMATIC_LICENSES_SAVE_FAILED'));
            }
        } catch {
            toast?.error(t('SCHEMATIC_LICENSES_SAVE_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (entry: SchematicLicenseDto) => {
        if (!await showConfirm(t('SCHEMATIC_LICENSES_CONFIRM_DELETE', entry.currentUsername), true)) return;
        try {
            const res = await api.schematicLicenseAdmin.deleteEntry(entry.id);
            if (isOk(res.data)) {
                toast?.info(t('SCHEMATIC_LICENSES_DELETED'));
                if (editingId() === entry.id) resetForm();
                reload();
            } else {
                toast?.error(t('SCHEMATIC_LICENSES_DELETE_FAILED'));
            }
        } catch {
            toast?.error(t('SCHEMATIC_LICENSES_DELETE_FAILED'));
        }
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="SCHEMATIC_LICENSES_TITLE" /></h1>
                <span class={styles.entryCount}>
                    {(licenses() ?? []).length} <Text val="SCHEMATIC_LICENSES_COUNT" />
                </span>
            </div>

            <p class={styles.intro}><Text val="SCHEMATIC_LICENSES_INTRO" /></p>

            <form class="fcard v" onSubmit={(e) => { e.preventDefault(); void submit(); }} style={{ gap: 'var(--spacing-md)' }}>
                <h2 style={{ margin: 0 }}>
                    <Text val={editingId() ? 'SCHEMATIC_LICENSES_EDIT_ENTRY' : 'SCHEMATIC_LICENSES_ADD_ENTRY'} />
                </h2>

                <div class={styles.formGrid}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="SCHEMATIC_LICENSES_FIELD_NAME" />
                        </span>
                        <input
                            type="text"
                            value={form.playerName}
                            disabled={!!editingId()}
                            onInput={(e) => setForm('playerName', e.currentTarget.value)}
                            placeholder={t('SCHEMATIC_LICENSES_FIELD_NAME_PLACEHOLDER')}
                        />
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="SCHEMATIC_LICENSES_FIELD_EXPIRES" />
                        </span>
                        <input
                            type="date"
                            value={form.expiresAt}
                            onInput={(e) => setForm('expiresAt', e.currentTarget.value)}
                        />
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="SCHEMATIC_LICENSES_FIELD_NOTE" />
                        </span>
                        <input
                            type="text"
                            value={form.note}
                            onInput={(e) => setForm('note', e.currentTarget.value)}
                            placeholder={t('SCHEMATIC_LICENSES_FIELD_NOTE_PLACEHOLDER')}
                        />
                    </label>
                </div>

                <Show when={editingId()}>
                    <label class={styles.toggle}>
                        <input type="checkbox" checked={form.isEnabled} onChange={(e) => setForm('isEnabled', e.currentTarget.checked)} />
                        <Text val="SCHEMATIC_LICENSES_FIELD_ENABLED" />
                    </label>
                </Show>

                <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                    <Show when={editingId()}>
                        <button type="button" class="btn btn-secondary" onClick={resetForm}>
                            <Text val="CANCEL" />
                        </button>
                    </Show>
                    <button type="submit" class="btn btn-primary" disabled={submitting() || (!editingId() && !form.playerName.trim())}>
                        <Show when={submitting()} fallback={<Text val={editingId() ? 'SAVE' : 'SCHEMATIC_LICENSES_ADD'} />}>
                            <i class="fa-solid fa-spinner fa-spin" />
                        </Show>
                    </button>
                </div>
            </form>

            <Show when={loading()}>
                <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show
                    when={(licenses() ?? []).length > 0}
                    fallback={<EmptyState icon="fa-key" title={t('SCHEMATIC_LICENSES_EMPTY')} hint={t('SCHEMATIC_LICENSES_EMPTY_HINT')} />}
                >
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                            <table class={styles.table}>
                                <thead>
                                    <tr>
                                        <th><Text val="SCHEMATIC_LICENSES_COL_PLAYER" /></th>
                                        <th><Text val="SCHEMATIC_LICENSES_COL_EXPIRES" /></th>
                                        <th><Text val="SCHEMATIC_LICENSES_COL_STATUS" /></th>
                                        <th><Text val="SCHEMATIC_LICENSES_COL_ISSUED" /></th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={licenses()}>{(entry) => {
                                        const status = () => statusOf(entry);
                                        return (
                                            <tr>
                                                <td>
                                                    <div class={styles.player}>
                                                        <img
                                                            class={styles.avatar}
                                                            src={`https://mc-heads.net/avatar/${entry.minecraftUuid}/32`}
                                                            alt={entry.currentUsername}
                                                        />
                                                        <div class="v" style={{ gap: '2px' }}>
                                                            <span class={styles.name}>{entry.currentUsername}</span>
                                                            <Show when={entry.note}>
                                                                <span class={styles.note}>{entry.note}</span>
                                                            </Show>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{entry.expiresAt ? formatDate(entry.expiresAt) : t('SCHEMATIC_LICENSES_NEVER_EXPIRES')}</td>
                                                <td>
                                                    <span class={`${styles.pill} ${styles[`pill${status().charAt(0).toUpperCase()}${status().slice(1)}`]}`}>
                                                        <Text val={STATUS_KEYS[status()]} />
                                                    </span>
                                                </td>
                                                <td>{formatDateTime(entry.createdAt)}</td>
                                                <td>
                                                    <div class={styles.rowActions}>
                                                        <button class="btn btn-secondary btn-sm" onClick={() => startEdit(entry)}>
                                                            <i class="fa-solid fa-pen-to-square" />
                                                        </button>
                                                        <button class="btn btn-danger btn-sm" onClick={() => void remove(entry)}>
                                                            <i class="fa-solid fa-trash" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }}</For>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
